import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { vlaLockApi } from "../api/client";
import { applicationStatusQuery } from "../features/review/api/useApplicationStatus";
import { resolveAllSectionPermissions } from "../lib/permissions/resolveSectionPermission";
import { isHttpError } from "../lib/query/errors";
import { useApprovalStore } from "../stores/approvalStore";
import { useRolePermissions } from "./useRolePermissions";

export interface FinancialAccessCheck {
  visible: boolean;
  editable: boolean;
}

/**
 * Re-confirms we still hold the VLA lock. Only a held lock can be lost, so the
 * check is skipped for every other phase. `acquireLock` is idempotent for the
 * holder (200) and answers 409 when someone else now owns it; any other failure
 * propagates so the caller fails safe.
 * @param {string} approvalId - The approval/VLA id of the open deal.
 */
async function confirmLockStillHeld(approvalId: string): Promise<void> {
  const { lock, setHeldByOther } = useApprovalStore.getState();
  if (lock.phase !== "held-by-me") {
    return;
  }
  try {
    await vlaLockApi.acquireLock(approvalId);
  } catch (error) {
    if (!isHttpError(error, 409)) {
      throw error;
    }
    setHeldByOther({});
  }
}

/**
 * Re-evaluates the Financial tab's access on demand (spec X2): the page's
 * permissions are computed from a status read that can be 30s stale and a lock
 * decided once at deal entry, so a status/phase/lock change while a popup is
 * open would otherwise go unnoticed. The returned check refetches the status
 * (which also refreshes the page's own permissions through the shared cache),
 * re-confirms the lock, and resolves the Financial verdict again. Call it
 * before opening an editable popup and before each Apply.
 *
 * Fails safe: if the status or lock can't be verified, the section stays
 * visible (it is already on screen) but is reported not editable.
 * @param {string} approvalId - The deal / approval identifier.
 * @returns {() => Promise<FinancialAccessCheck>} A function resolving the fresh Financial verdict.
 */
export function useFinancialAccessGuard(approvalId: string): () => Promise<FinancialAccessCheck> {
  const queryClient = useQueryClient();
  const { capabilities } = useRolePermissions();

  return useCallback(async () => {
    try {
      const [status] = await Promise.all([
        queryClient.fetchQuery({ ...applicationStatusQuery(approvalId), staleTime: 0 }),
        confirmLockStillHeld(approvalId),
      ]);
      const isVlaHeldByMe = useApprovalStore.getState().lock.phase === "held-by-me";
      const { financial } = resolveAllSectionPermissions(capabilities, status, isVlaHeldByMe);
      return { visible: financial.visible, editable: financial.editable };
    } catch {
      return { visible: true, editable: false };
    }
  }, [approvalId, capabilities, queryClient]);
}
